import { useCallback, useRef } from 'react';
import { useStore } from 'react-redux';
import type { Currency } from '../../../prop-types/currency-types';
import { Segment } from '../../../services/analytics-services/segment';
import { v4 } from 'uuid';
import {
  SHOWED_RATE_SHOPPER_RECOMMENDATION,
  APPLIED_RATE_SHOPPER_RECOMMENDATION,
  SHOW_FROM_CSW,
  APPLY_FROM_CSW,
} from '../../../constants/analytics-events';
import { trackShowRecommendation, trackApplyRecommendation } from '../rate-recommendations.api';
import { RATE_BROWSER, CUSTOMERS_CREATE_LABEL } from '../../../constants/fulfillment-plan-ids';
import { RecommendationType } from '../recommendation-type';
import type { ActionLocationType, SaveShowedRecommendationRequest } from '../types/rate-recommendation-api-types';
import { createRateComparisonMetadata } from '../create-rate-comparison-metadata';
import { redactPIIFromShippingConfiguration } from '../../../services/redacted-information-service';
import { getInsuranceProviderById } from '../../../enums/insurance-providers';

type RecommendationShownParams = {
  amountCheaperValue: number;
  amountCheaperCode: string;
  serviceCollectionGuid: string;
  serviceId: string;
  sellerLabelProviderId: string;
  carrierId: string;
  insuranceProviderId: string;
  rateShopperTotalCost: Currency;
  userRatesTotalCost: Currency;
};

type ShippingConfiguration = {
  serviceId: string;
  sellerLabelProviderId: string;
  carrierId: string;
  insuranceProvider: string;
  shippingConfigurationHash: string;
};

type TrackingData = {
  showedRecommendationSegmentEventData: Record<string, unknown>;
  sellerId: string;
  showedRecommendationRequest: SaveShowedRecommendationRequest;
  originalShippingConfiguration: ShippingConfiguration;
  userRatesTotalCost: Currency;
};

// Captures tracking metadata when a recommendation is shown, stores it in a ref, and reuses that same data when the
// recommendation is applied. Call resetTracking at the end of the flow to clear the stored data.
export const useRateShopperRecommendationTracking = (fulfillmentPlanId: string) => {
  const trackingData = useRef<TrackingData | null>(null);
  const { getState } = useStore();

  const trackRecommendationShown = useCallback(
    ({
      amountCheaperValue,
      amountCheaperCode,
      serviceCollectionGuid,
      serviceId,
      sellerLabelProviderId,
      carrierId,
      insuranceProviderId,
      rateShopperTotalCost,
      userRatesTotalCost,
    }: RecommendationShownParams) => {
      const { sellerId, originalShippingConfiguration } = createRateComparisonMetadata(getState(), fulfillmentPlanId);

      const showedRecommendationSegmentEventData = {
        action: SHOW_FROM_CSW,
        eventId: v4(),
        fulfillmentPlanId,
        amountCheaperValue,
        amountCheaperCode,
        originalShippingConfiguration: redactPIIFromShippingConfiguration(originalShippingConfiguration),
        suggestedServiceCollectionId: serviceCollectionGuid,
      };

      Segment.trackEvent(SHOWED_RATE_SHOPPER_RECOMMENDATION, showedRecommendationSegmentEventData, { sellerId });

      const insuranceProvider = getInsuranceProviderById(insuranceProviderId);
      const actionLocationType: ActionLocationType = 'CSW';

      const showedRecommendationRequest = {
        fulfillmentPlanId,
        toServiceCollectionGuid: serviceCollectionGuid,
        toServiceId: serviceId,
        toSellerLabelProviderId: sellerLabelProviderId,
        toCarrierId: carrierId,
        toInsuranceProvider: insuranceProvider?.name ?? '',
        recommendationType: RecommendationType.RateShopper,
        toTotalCost: rateShopperTotalCost,
        actionLocationType,
      };

      if (fulfillmentPlanId !== RATE_BROWSER && fulfillmentPlanId !== CUSTOMERS_CREATE_LABEL) {
        trackShowRecommendation(showedRecommendationRequest);
      }

      trackingData.current = {
        showedRecommendationSegmentEventData,
        sellerId,
        showedRecommendationRequest,
        originalShippingConfiguration,
        userRatesTotalCost,
      };
    },
    [fulfillmentPlanId, getState],
  );

  const trackRecommendationApplied = useCallback(() => {
    if (!trackingData.current) {
      return;
    }

    const {
      showedRecommendationRequest,
      showedRecommendationSegmentEventData,
      sellerId,
      originalShippingConfiguration,
      userRatesTotalCost,
    } = trackingData.current;

    trackApplyRecommendation({
      ...showedRecommendationRequest,
      fromServiceId: originalShippingConfiguration.serviceId,
      fromSellerLabelProviderId: originalShippingConfiguration.sellerLabelProviderId,
      fromCarrierId: originalShippingConfiguration.carrierId,
      fromInsuranceProvider: originalShippingConfiguration.insuranceProvider,
      fromTotalCost: userRatesTotalCost,
      fromShippingConfigurationHash: originalShippingConfiguration.shippingConfigurationHash,
    });

    Segment.trackEvent(
      APPLIED_RATE_SHOPPER_RECOMMENDATION,
      {
        ...showedRecommendationSegmentEventData,
        action: APPLY_FROM_CSW,
      },
      { sellerId },
    );
  }, []);

  const resetTracking = useCallback(() => {
    trackingData.current = null;
  }, []);

  return {
    trackRecommendationShown,
    trackRecommendationApplied,
    resetTracking,
  };
};
